import React, { useEffect, useRef, useState } from 'react';
import { useAPI } from '../../contexts/APIContext';

// Character voices, matched to the iOS picker Pranit approved: mascot badges
// in a grid of four, no age shelves, a short list (Surprise + 7 at creation,
// 8 when changing), "Best match" from the vision shortlist when there is a
// picture to match against, and "Record your voice" above the grid.
//
// value (creation): { type: 'random' } | { type: 'preset', voice_id, name } | { type: 'recorded', file }

export const FREDOKA = "'Fredoka', 'Inter', sans-serif";
export const VX = {
  ink: '#0B1A33',
  inkSoft: 'rgba(11,26,51,0.6)',
  blue: '#0065F8',
  blueDeep: '#073B8E',
  sky: '#AFE3FE',
  gold: '#FFC337',
  pink: '#FF6B9A',
};
export const PIP_STUDIO_ART =
  'https://huzyagtfxwapsccsksib.supabase.co/storage/v1/object/public/videos/assets/pip_email/pip_studio_sm.jpg';

export function useVoiceOptions() {
  const { apiClient } = useAPI();
  const [voices, setVoices] = useState([]);
  const [cost, setCost] = useState(10);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiClient.getVoiceOptions();
        if (cancelled) return;
        setVoices(r?.voices || []);
        if (r?.change_cost_stars) setCost(r.change_cost_stars);
      } catch (_) {
        /* keep empty: the picker hides itself */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiClient]);
  return { voices, cost, loading };
}

/**
 * The vision shortlist for a character picture ({suggested, alternatives}).
 * Gets at most `waitMs`: whatever arrives by then is used once, so the grid
 * never reshuffles under a finger when a slow shortlist lands later.
 */
export function useVoiceShortlist(imageUrl, waitMs = 4000) {
  const { apiClient } = useAPI();
  const [state, setState] = useState({ ready: !imageUrl, suggested: null, alternatives: [] });
  useEffect(() => {
    if (!imageUrl) {
      setState({ ready: true, suggested: null, alternatives: [] });
      return undefined;
    }
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      setState({ ready: true, ...value });
    };
    const timer = setTimeout(() => finish({ suggested: null, alternatives: [] }), waitMs);
    apiClient
      .getVoiceSuggestions(imageUrl)
      .then((r) => finish({ suggested: r?.suggested || null, alternatives: r?.alternatives || [] }))
      .catch(() => finish({ suggested: null, alternatives: [] }));
    return () => {
      settled = true;
      clearTimeout(timer);
    };
  }, [apiClient, imageUrl, waitMs]);
  return state;
}

/** Best match first, then the shortlist, then the rest of the catalog. */
export function orderVoices(catalog, shortlist) {
  const seen = new Set();
  const out = [];
  [shortlist?.suggested, ...(shortlist?.alternatives || []), ...(catalog || [])].forEach((v) => {
    if (v && v.voice_id && !seen.has(v.voice_id)) {
      seen.add(v.voice_id);
      // prefer the catalog row (it carries mascot_url / preview_url)
      out.push((catalog || []).find((c) => c.voice_id === v.voice_id) || v);
    }
  });
  return out;
}

/** One audio element for the whole picker: playing a voice stops the last one. */
export function usePreviewPlayer() {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(null);
  useEffect(() => () => audioRef.current?.pause(), []);
  const stop = () => {
    audioRef.current?.pause();
    setPlaying(null);
  };
  const toggle = (voice) => {
    if (!voice?.preview_url) return;
    if (playing === voice.voice_id) {
      stop();
      return;
    }
    audioRef.current?.pause();
    const a = new Audio(voice.preview_url);
    a.onended = () => setPlaying((p) => (p === voice.voice_id ? null : p));
    a.onerror = () => setPlaying(null);
    audioRef.current = a;
    a.play().then(() => setPlaying(voice.voice_id)).catch(() => setPlaying(null));
  };
  return { playing, toggle, stop };
}

const css = `
.vx-badge { transition: transform 160ms cubic-bezier(0.34,1.56,0.64,1); }
.vx-badge:hover { transform: translateY(-2px); }
.vx-badge:active { transform: scale(0.95); }
.vx-badge:focus-visible { outline: 3px solid #0065F8; outline-offset: 4px; border-radius: 16px; }
.vx-press { transition: transform 120ms ease, box-shadow 120ms ease; }
.vx-press:active { transform: translateY(3px); }
@media (prefers-reduced-motion: reduce) { .vx-badge, .vx-press { transition: none; } }
`;
export const VoiceStyles = () => <style>{css}</style>;

function Pill({ text, fill, color }) {
  return (
    <span
      style={{
        fontFamily: FREDOKA,
        fontSize: 11,
        fontWeight: 600,
        lineHeight: '15px',
        padding: '1px 7px',
        borderRadius: 999,
        background: fill,
        color,
        whiteSpace: 'nowrap',
        visibility: text ? 'visible' : 'hidden',
      }}
    >
      {text || 'Now'}
    </span>
  );
}

/** A voice as a mascot badge: tap to hear it and pick it. */
export function VoiceBadge({ voice, selected, suggested, current, playing, onPick, size = 64 }) {
  return (
    <button
      type="button"
      className="vx-badge"
      role="radio"
      aria-checked={selected}
      aria-label={`${voice.name}${suggested ? ', best match' : ''}${current ? ', current voice' : ''}`}
      onClick={() => onPick(voice)}
      style={{
        border: 'none',
        background: 'transparent',
        padding: 0,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 5,
        fontFamily: FREDOKA,
        transform: selected ? 'scale(1.05)' : undefined,
      }}
    >
      <span
        style={{
          position: 'relative',
          width: size,
          height: size,
          borderRadius: '50%',
          background: 'rgba(175,227,254,0.5)',
          boxShadow: `0 0 0 ${selected ? 3 : 1.5}px ${selected ? VX.blue : 'rgba(11,26,51,0.14)'}`,
        }}
      >
        {voice.mascot_url && (
          <img
            src={voice.mascot_url}
            alt=""
            loading="lazy"
            style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover', display: 'block' }}
          />
        )}
        {selected && (
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: -3,
              right: -2,
              width: 20,
              height: 20,
              borderRadius: '50%',
              background: '#fff',
              border: `1.4px solid ${VX.ink}`,
              color: VX.gold,
              fontSize: 12,
              lineHeight: '18px',
              textAlign: 'center',
            }}
          >
            ★
          </span>
        )}
        {playing && (
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              bottom: -2,
              right: -2,
              width: 22,
              height: 22,
              borderRadius: '50%',
              background: VX.blue,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3a4.5 4.5 0 00-2.5-4.03v8.05A4.5 4.5 0 0016.5 12z" />
            </svg>
          </span>
        )}
      </span>
      <span
        style={{
          fontSize: 14,
          fontWeight: selected ? 700 : 500,
          color: selected ? VX.blue : VX.ink,
          maxWidth: size + 24,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {voice.name}
      </span>
      {suggested ? (
        <Pill text="Best match" fill="rgba(255,195,55,0.9)" color={VX.ink} />
      ) : current ? (
        <Pill text="Now" fill={VX.ink} color="#fff" />
      ) : (
        <Pill text="" />
      )}
    </button>
  );
}

/** Grid of four across; `leading` goes first (e.g. the Surprise badge). */
export function VoiceBadgeGrid({ voices, suggestedId, currentId, selectedId, playing, onPick, leading = null, size = 64 }) {
  return (
    <div role="radiogroup" aria-label="Voices" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '16px 10px' }}>
      {leading}
      {voices.map((v) => (
        <VoiceBadge
          key={v.voice_id}
          voice={v}
          size={size}
          selected={selectedId === v.voice_id}
          suggested={suggestedId === v.voice_id}
          current={currentId === v.voice_id}
          playing={playing === v.voice_id}
          onPick={onPick}
        />
      ))}
    </div>
  );
}

export function SurpriseBadge({ selected, onPick, size = 64 }) {
  return (
    <button
      type="button"
      className="vx-badge"
      role="radio"
      aria-checked={selected}
      aria-label="Surprise me: Pip picks a voice that fits"
      onClick={onPick}
      style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, fontFamily: FREDOKA }}
    >
      <span
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #FFD76A, #FF9AC2)',
          boxShadow: `0 0 0 ${selected ? 3 : 1.5}px ${selected ? VX.blue : 'rgba(11,26,51,0.14)'}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: size * 0.42,
        }}
        aria-hidden="true"
      >
        🎲
      </span>
      <span style={{ fontSize: 14, fontWeight: selected ? 700 : 500, color: selected ? VX.blue : VX.ink }}>Surprise</span>
      <Pill text="" />
    </button>
  );
}

/** The blue "Record your voice" bar used at creation and in the voice window. */
export function RecordVoiceButton({ ready, onClick, label }) {
  return (
    <button
      type="button"
      className="vx-press"
      onClick={onClick}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '12px 16px',
        borderRadius: 16,
        border: `2px solid ${VX.ink}`,
        background: ready ? '#1FA85B' : VX.blue,
        boxShadow: `0 4px 0 ${ready ? '#0E6B38' : VX.blueDeep}`,
        color: '#fff',
        fontFamily: FREDOKA,
        fontSize: 16,
        fontWeight: 700,
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      <span aria-hidden="true" style={{ fontSize: 18 }}>{ready ? '✓' : '🎙'}</span>
      <span style={{ flex: 1 }}>{label || (ready ? 'Your voice is ready!' : 'Record your voice')}</span>
      <span aria-hidden="true" style={{ opacity: 0.85 }}>›</span>
    </button>
  );
}

/** The sky strip with Pip at the mic and a two-line title. */
export function PipStudioHeader({ line1, line2, height = 104 }) {
  return (
    <div style={{ position: 'relative', height, background: VX.sky, overflow: 'hidden' }}>
      <img src={PIP_STUDIO_ART} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      <div style={{ position: 'absolute', top: 18, right: 18, textAlign: 'right', fontFamily: FREDOKA, fontWeight: 700, fontSize: 24, lineHeight: 1.05 }}>
        <div style={{ color: VX.ink }}>{line1}</div>
        <div style={{ color: VX.blue }}>{line2}</div>
      </div>
    </div>
  );
}

/**
 * Character creation: "Record your voice", then Surprise + 7 voices. There is
 * no picture yet on web (it is drawn after Create), so no Best match here.
 */
export default function VoicePicker({ value, onChange, onRecord, imageUrl = null }) {
  const { voices, loading } = useVoiceOptions();
  const { playing, toggle } = usePreviewPlayer();
  // With the character's picture: the Best match leads and starts selected,
  // like the iOS creation step.
  const shortlist = useVoiceShortlist(imageUrl);
  const suggested = imageUrl ? shortlist.suggested : null;
  useEffect(() => {
    if (!value && suggested?.voice_id) {
      onChange({ type: 'preset', voice_id: suggested.voice_id, name: suggested.name });
    }
  }, [suggested?.voice_id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!loading && !voices.length) return null;
  const grid = orderVoices(voices, imageUrl ? shortlist : null).slice(0, 7);
  const recorded = value?.type === 'recorded';

  return (
    <div
      style={{
        borderRadius: 22,
        overflow: 'hidden',
        border: `2.5px solid ${VX.ink}`,
        boxShadow: `0 4px 0 ${VX.ink}`,
        background: '#fff',
        fontFamily: FREDOKA,
      }}
    >
      <VoiceStyles />
      <PipStudioHeader line1="Pick their" line2="voice!" />
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {onRecord && <RecordVoiceButton ready={recorded} onClick={onRecord} />}
        {loading || !shortlist.ready ? (
          <div style={{ textAlign: 'center', color: VX.inkSoft, fontSize: 14, padding: '32px 0' }}>Finding voices that fit…</div>
        ) : (
          <VoiceBadgeGrid
            voices={grid}
            suggestedId={suggested?.voice_id}
            selectedId={value?.type === 'preset' ? value.voice_id : null}
            playing={playing}
            size={58}
            onPick={(v) => {
              toggle(v);
              onChange({ type: 'preset', voice_id: v.voice_id, name: v.name });
            }}
            leading={<SurpriseBadge size={58} selected={value?.type === 'random'} onPick={() => onChange({ type: 'random' })} />}
          />
        )}
      </div>
    </div>
  );
}
